import { createSelector } from 'reselect';
import cartUtils from '../utils/cart';
import {
  selectRetouchingAddableForOrder,
  selectRetouchingLevelsForOrder,
} from './retouchingGate';
import { RetouchingProduct } from '../components/v3/products/retouching/types';
import type { RetouchingRootState } from './retouchingState';
import {
  getProductOptionSets,
  productOptionArgs,
} from '../components/v3/products/regular/subcomponents/productOptionsHelpers';

// What a first photo's options cost on a level the order has no line on yet:
// the choices the retouching screen would preselect.
const newLineOptionCents = (product: RetouchingProduct): number => {
  const { productOptions = {}, productOptionKey = null } =
    productOptionArgs(product);
  const fields: { selectedProductOptions?: { optionPrice?: number }[] } =
    cartUtils.productOptionCartFields(
      product,
      productOptions,
      productOptionKey,
    );
  return (fields.selectedProductOptions || []).reduce(
    (sum, option) => sum + (option.optionPrice || 0),
    0,
  );
};

export interface RetouchingChipLevel {
  productId: number;
  name: string;
  priceCents: number;
  // Adding the photo needs the retouching screen rather than happening in
  // place: the level requires a note per photo, or the order has no line on
  // it yet and the level has product options to choose.
  opensPicker: boolean;
}

// What a cart photo's retouching chip shows: the level the photo is already
// on, or the levels it can be added to at the price of the order's next photo
// on each. Null when the photo gets no chip.
export type RetouchingChip =
  | { kind: 'retouched'; productId: number; levelName: string }
  | { kind: 'add'; levels: RetouchingChipLevel[] };

interface ChipCartItem {
  id?: string;
  productId?: number;
  productName?: string;
  isRetouching?: boolean;
  poseIds?: (number | string)[];
}

interface ChipOrder {
  cartItems?: ChipCartItem[];
}

const EMPTY_LEVELS: RetouchingProduct[] = [];

// A null key never falls back to the order being built: the chip only ever
// works on the cart order it is rendered in.
type State = RetouchingRootState;

const selectChipOrder = (
  state: State,
  orderKey: string | null,
): ChipOrder | undefined =>
  orderKey == null ? undefined : state.v3Order?.orders?.[orderKey];
const selectChipAddable = (state: State, orderKey: string | null): boolean =>
  orderKey != null && selectRetouchingAddableForOrder(state, orderKey);
const selectChipLevels = (
  state: State,
  orderKey: string | null,
): RetouchingProduct[] =>
  orderKey == null
    ? EMPTY_LEVELS
    : selectRetouchingLevelsForOrder(state, orderKey);
const selectChipPoseId = (
  _state: State,
  _orderKey: string | null,
  poseId: number | null | undefined,
) => poseId;

export const selectRetouchingChipForPose = createSelector(
  selectChipOrder,
  selectChipAddable,
  selectChipLevels,
  selectChipPoseId,
  (order, addable, levels, poseId): RetouchingChip | null => {
    if (!order || !addable || poseId == null) return null;

    // Only a photo the order could select gets a chip: never a group photo,
    // never an id that is not on one of the order's print lines.
    const selectable = cartUtils
      .distinctCartPoses(order)
      .some(
        (pose: { id: number | string }) => String(pose.id) === String(poseId),
      );
    if (!selectable) return null;

    const onLine = cartUtils.retouchingLineForPose(order, poseId);
    if (onLine) {
      const level = levels.find((product) => product.id === onLine.productId);
      return {
        kind: 'retouched',
        productId: onLine.productId,
        levelName: level?.name ?? onLine.cartItem.productName ?? 'Retouching',
      };
    }

    return {
      kind: 'add',
      levels: levels.map((product) => {
        const line = (order.cartItems || []).find(
          (item) => item.isRetouching && item.productId === product.id,
        );
        return {
          productId: product.id,
          name: product.name,
          priceCents: cartUtils.marginalRetouchingPriceForNext({
            product,
            cartItem: line,
            newLineOptionCents: line ? 0 : newLineOptionCents(product),
          }),
          opensPicker:
            product.retouching_note_mode === 'required' ||
            (!line && getProductOptionSets(product).length > 0),
        };
      }),
    };
  },
);
